import type { ComponentPropsWithoutRef, CSSProperties, ReactNode } from "react";

import clsx from "clsx";

type ModalPanelProps = ComponentPropsWithoutRef<"div"> & {
  children: ReactNode;
  maxHeight?: CSSProperties["maxHeight"];
};

const ModalPanelRoot = ({
  children,
  className,
  maxHeight,
  style,
  ...props
}: ModalPanelProps) => (
  <div
    className={clsx(
      "flex max-h-full flex-col gap-6 overflow-hidden",
      className,
    )}
    style={{
      ...style,
      ...(maxHeight !== undefined ? { maxHeight } : {}),
    }}
    {...props}
  >
    {children}
  </div>
);

type ModalPanelHeaderProps = ComponentPropsWithoutRef<"header"> & {
  children: ReactNode;
};

export const ModalPanelHeader = ({
  children,
  className,
  ...props
}: ModalPanelHeaderProps) => (
  <header className={clsx("flex shrink-0 flex-col", className)} {...props}>
    {children}
  </header>
);

type ModalPanelBodyProps = ComponentPropsWithoutRef<"div"> & {
  children: ReactNode;
  scrollable?: boolean;
};

export const ModalPanelBody = ({
  children,
  className,
  scrollable = false,
  ...props
}: ModalPanelBodyProps) => (
  <div
    className={clsx(
      "flex min-h-0 w-full flex-1",
      scrollable ? "overflow-auto" : "overflow-hidden",
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

type ModalPanelFooterProps = ComponentPropsWithoutRef<"footer"> & {
  children: ReactNode;
};

export const ModalPanelFooter = ({
  children,
  className,
  ...props
}: ModalPanelFooterProps) => (
  <footer
    className={clsx("flex shrink-0 justify-center", className)}
    {...props}
  >
    {children}
  </footer>
);

export const ModalPanel = Object.assign(ModalPanelRoot, {
  Header: ModalPanelHeader,
  Body: ModalPanelBody,
  Footer: ModalPanelFooter,
});
